import { useEffect, useState, useCallback } from "react";
import { Search, RefreshCw, UserPlus, MessageSquarePlus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export function StatusBadge({ status }) {
  const map = {
    new: "bg-[#8EC5E8]/15 text-[#4E8DB5] border-[#8EC5E8]/40",
    contacted: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    qualified: "bg-violet-500/10 text-violet-600 border-violet-500/30",
    in_progress: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    converted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    lost: "bg-slate-500/10 text-slate-500 border-slate-400/30",
  };
  const labels = {
    new: ["Neu", "New"], contacted: ["Kontaktiert", "Contacted"], qualified: ["Qualifiziert", "Qualified"],
    in_progress: ["In Bearbeitung", "In progress"], converted: ["Gewonnen", "Won"], lost: ["Verloren", "Lost"],
  };
  const lang = localStorage.getItem("eleven_lang") || "de";
  return (
    <Badge variant="outline" className={`rounded-full text-[10px] font-medium ${map[status] || map.new}`}>
      {(labels[status] || labels.new)[lang === "de" ? 0 : 1]}
    </Badge>
  );
}

const STATUSES = ["new", "contacted", "qualified", "in_progress", "converted", "lost"];

export default function Leads() {
  const { t } = useLang();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pipeline, setPipeline] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const canAssign = ["super_admin", "admin", "manager", "backoffice"].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (pipeline !== "all") params.pipeline = pipeline;
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/leads", { params }).then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [pipeline, status, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canAssign) {
      api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.status === "active"))).catch(() => {
        // manager/backoffice may not access admin users; fallback silently
        setUsers([]);
      });
    }
  }, [canAssign]);

  const updateLead = async (leadId, changes) => {
    try {
      const res = await api.patch(`/leads/${leadId}`, changes);
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, ...res.data } : l)));
      if (selected?.id === leadId) setSelected((s) => ({ ...s, ...res.data }));
      toast.success(t("Lead aktualisiert", "Lead updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Aktualisierung fehlgeschlagen", "Update failed"));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const res = await api.post(`/leads/${selected.id}/notes`, { text: note });
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), res.data] }));
      setNote("");
      toast.success(t("Notiz gespeichert", "Note saved"));
    } catch (e) {
      toast.error(t("Notiz konnte nicht gespeichert werden", "Note could not be saved"));
    }
  };

  const pipelines = ["Nettopolicen", "Immobilien", "Festgeld", "Beraterbewerbungen", "Partneranfragen", "Mandanten Allgemein"];

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Leads</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.role === "berater" ? t("Ihre zugewiesenen Leads", "Your assigned leads") : t("Alle Leads der Plattform", "All platform leads")}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-[10px]" onClick={load} data-testid="leads-refresh-button">
          <RefreshCw className="h-4 w-4 mr-1.5" /> {t("Aktualisieren", "Refresh")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t("Suchen\u2026", "Search\u2026")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-[10px]" data-testid="leads-search-input" />
        </div>
        <Select value={pipeline} onValueChange={setPipeline}>
          <SelectTrigger className="w-[200px] rounded-[10px]" data-testid="leads-pipeline-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Alle Pipelines", "All pipelines")}</SelectItem>
            {pipelines.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[170px] rounded-[10px]" data-testid="leads-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Alle Status", "All statuses")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-[16px] overflow-hidden">
        {loading ? (
          <CardContent className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </CardContent>
        ) : leads.length === 0 ? (
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground text-sm">{t("Keine Leads gefunden.", "No leads found.")}</p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Name", "Name")}</TableHead>
                  <TableHead>Pipeline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">{t("Quelle", "Source")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("Zugewiesen", "Assigned")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("Datum", "Date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <p className="text-sm font-medium">{l.first_name} {l.last_name}</p>
                      <p className="text-xs text-muted-foreground">{l.email}</p>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="rounded-full text-[10px] font-normal">{l.pipeline}</Badge></TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{l.utm?.source || l.source}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">{l.assigned_to_name || "\u2014"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-muted-foreground font-mono-num">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl" data-testid="lead-detail-name">{selected.first_name} {selected.last_name}</DialogTitle>
                <DialogDescription>{selected.email} · {selected.phone || t("kein Telefon", "no phone")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Pipeline</p>
                  <p className="mt-1 font-medium">{selected.pipeline}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t("Anfrageart", "Request type")}</p>
                  <p className="mt-1 font-medium">{selected.form_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Status</p>
                  <Select value={selected.status} onValueChange={(v) => updateLead(selected.id, { status: v })}>
                    <SelectTrigger className="mt-1 rounded-[10px] h-9" data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {canAssign && users.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t("Zugewiesen an", "Assigned to")}</p>
                    <Select value={selected.assigned_to || "none"} onValueChange={(v) => updateLead(selected.id, { assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1 rounded-[10px] h-9" data-testid="lead-assign-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("Niemand", "Nobody")}</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground uppercase">UTM / {t("Quelle", "Source")}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Badge variant="secondary" className="rounded-full text-[10px] font-normal">source: {selected.utm?.source || selected.source || "\u2014"}</Badge>
                    {selected.utm?.medium && <Badge variant="secondary" className="rounded-full text-[10px] font-normal">medium: {selected.utm.medium}</Badge>}
                    {selected.utm?.campaign && <Badge variant="secondary" className="rounded-full text-[10px] font-normal">campaign: {selected.utm.campaign}</Badge>}
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground uppercase">Tags</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(selected.tags || []).map((tag) => (
                      <Badge key={tag} variant="outline" className="rounded-full text-[10px] font-mono-num">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground uppercase">{t("Einwilligungen", "Consents")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("Datenschutz", "Privacy")}: {selected.consent?.privacy ? "\u2713" : "\u2717"} · {t("Kontakt", "Contact")}: {selected.consent?.contact ? "\u2713" : "\u2717"} · Marketing: {selected.consent?.marketing ? "\u2713" : "\u2717"} · {selected.consent?.timestamp ? new Date(selected.consent.timestamp).toLocaleString() : ""}
                  </p>
                </div>
                {selected.message && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground uppercase">{t("Nachricht", "Message")}</p>
                    <p className="mt-1 text-sm leading-relaxed">{selected.message}</p>
                  </div>
                )}
              </div>
              <div className="border-t border-border pt-4">
                <p className="text-xs text-muted-foreground uppercase mb-2">{t("Notizen", "Notes")}</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(selected.notes || []).length === 0 && <p className="text-xs text-muted-foreground">{t("Keine Notizen.", "No notes.")}</p>}
                  {(selected.notes || []).map((n) => (
                    <div key={n.id} className="rounded-[10px] bg-muted p-3">
                      <p className="text-sm">{n.text}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Notiz hinzuf\u00fcgen\u2026", "Add note\u2026")} className="rounded-[10px] text-sm" data-testid="lead-note-input" />
                  <Button onClick={addNote} size="icon" className="rounded-[10px] bg-[#0B1220] shrink-0 self-end" data-testid="lead-note-submit">
                    <MessageSquarePlus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
